import React from 'react';
import useSeo from '../lib/useSeo';
import { Link } from 'react-router-dom';
import Navbar from '../components/site/Navbar';
import Footer from '../components/site/Footer';
import CountUp from '../components/site/CountUp';
import Reveal from '../components/site/Reveal';
import { track, events } from '../lib/analytics';
import {
  Sparkles,
  Target,
  ShieldCheck,
  Brain,
  Compass,
  Zap,
  Building2,
  BarChart3,
  Users,
  Rocket,
  ArrowRight,
} from 'lucide-react';

const aboutStats = [
  { value: '247%', label: 'Higher conversion rate', sub: 'with AALIGN nurture automation' },
  { value: '48 hrs', label: 'From conversation to first campaign live', sub: '(not weeks)' },
  { value: '65%', label: 'Margin on lead gen revenue', sub: '(vs 35% on programmatic)' },
  { value: '165M+', label: 'B2B professionals', sub: 'in your sourcing network' },
];

const positioning = [
  {
    icon: Sparkles,
    tag: 'What we do',
    accent: 'blue',
    title: 'Publishers become demand-gen businesses overnight.',
    body:
      'We turn B2B publishers into demand generation businesses overnight. White-label, fully operational, running under your brand. Your advertisers get qualified leads. You keep the margin. We handle everything else.',
  },
  {
    icon: ShieldCheck,
    tag: "What we're not",
    accent: 'pink',
    title: 'Not a lead list. Not a competitor. Not a brand in the room.',
    body:
      "We're not a lead list vendor selling cold contacts. We're not competing with you. We're not taking client relationships or putting our brand on anything. We're the engine room — invisible, operational, entirely yours.",
  },
  {
    icon: Users,
    tag: 'Who needs this',
    accent: 'blue',
    title: 'Publishers watching budgets leave for demand-gen vendors.',
    body:
      'B2B publishers watching advertisers take budget to demand gen vendors. Publishers with advertiser relationships but no lead gen offering. Publishers who want a new 65% margin revenue line without hiring an SDR team or building technology from scratch.',
  },
  {
    icon: Rocket,
    tag: 'The outcome',
    accent: 'teal',
    title: 'Revenue in 48 hours. Margin, brand and data stay yours.',
    body:
      'Revenue in 48 hours. Fully operational demand gen running under your brand, powering your advertiser relationships. First-party data stays yours. Margin is yours. Advertiser stays yours. All the operational work stays with us. You scale revenue without scaling headcount.',
  },
  {
    icon: Zap,
    tag: 'Why now',
    accent: 'pink',
    title: 'If you don\'t offer it, they\'ll buy it from someone else.',
    body:
      "Programmatic CPMs are falling. Search traffic is dying. 64% of publishers face declining subscriber revenue. Advertisers are moving budgets to demand generation — right now, today. If you don't offer it, they'll buy it from someone else. And you'll lose the relationship.",
  },
];

const posAccent = {
  blue: { icon: 'from-brand-blue to-brand-blue-light', shadow: 'shadow-mk-blue', bar: 'from-brand-blue to-brand-blue-light', text: 'text-brand-blue' },
  pink: { icon: 'from-brand-pink to-brand-pink/70', shadow: 'shadow-[0_4px_11px_0_rgba(233,30,99,0.35)]', bar: 'from-brand-pink to-brand-pink/70', text: 'text-brand-pink' },
  teal: { icon: 'from-teal-600 to-teal-400', shadow: 'shadow-[0_4px_11px_0_rgba(20,184,166,0.35)]', bar: 'from-teal-500 to-teal-400', text: 'text-teal-600' },
};

const engineFeatures = [
  {
    icon: Target,
    tag: 'Core differentiator',
    title: 'Two scores. Not one.',
    body:
      'Generic intent tools tell you a company is active. They don\u2019t tell you if they\u2019re right for your client. Enrich IQ scores both \u2014 opportunity (live signals: hiring, funding, news, tech stack) and personalised fit against your client\u2019s specific offering, with transparent rationale.',
  },
  {
    icon: Brain,
    tag: 'Per lead',
    title: 'Complete intelligence dossier',
    body:
      'Tech stack signals, real-time intent, verified contacts, buying committee mapping, engagement playbook with timing windows and personalised first-line openers. Not a row in a spreadsheet \u2014 a sales-ready package.',
  },
  {
    icon: Zap,
    tag: 'Built in',
    title: 'AI outreach',
    body:
      'Enrich IQ drafts personalised emails from enrichment data, sends from your inbox, tracks opens and replies, and auto-pauses sequences on engagement. Data to outbox in three clicks.',
  },
  {
    icon: ShieldCheck,
    tag: 'Verification',
    title: 'Multi-layer accuracy',
    body:
      'Directory match, deliverability check, smart fallback. Every lead verified before a client ever sees it. Your partner\u2019s brand goes on every deliverable \u2014 it has to be right.',
  },
];

const ecosystem = [
  {
    status: 'Live',
    name: 'Enrich IQ',
    body:
      'Every Lead, a Complete Dossier. Six-layer intelligence per lead — tech stack, real-time intent, verified contacts, buying committee, engagement playbook, personalised fit — dual-scored and ready in seconds.',
    icon: Brain,
    metrics: [
      { k: 'Signals per lead', v: '6 layers' },
      { k: 'Verify layers', v: '3' },
      { k: 'Fit scale', v: '0\u2013100' },
    ],
  },
  {
    status: 'Live',
    name: 'AALIGN',
    body:
      'The AI-powered lead management platform we license to publishers. Capture, nurture, convert. Automated workflows, real-time analytics, advanced reporting — built on enterprise-grade security (GDPR, CCPA, SOC 2 Type II).',
    icon: Building2,
    metrics: [
      { k: 'Leads processed', v: '5M+' },
      { k: 'Active users', v: '300+' },
      { k: 'Rating', v: '4.9/5' },
    ],
  },
  {
    status: 'Live',
    name: 'Audience Intelligence',
    body:
      'Publisher-branded corporate intelligence briefings that map named decision-makers, size material contract renewals, chart the competitive set, and hand the advertiser\u2019s sales team a dated path to close.',
    icon: BarChart3,
    metrics: [
      { k: 'Format', v: '13-page briefing' },
      { k: 'Source classes', v: '4' },
      { k: 'Horizon', v: '12-week path' },
    ],
  },
];

const principles = [
  {
    icon: Target,
    title: 'Specificity over abstraction',
    body:
      'Never say "solutions" when we can name what it does. "Dual-scores opportunity and personalised fit with transparent rationale" beats "intelligent lead enrichment" every time.',
  },
  {
    icon: BarChart3,
    title: 'Proof over promise',
    body:
      'Numbers before adjectives. £420K+ new revenue in Year 1. 5 days to first campaign live. 100% of the margin retained by the publisher. If we can\u2019t prove it, we don\u2019t say it.',
  },
  {
    icon: ShieldCheck,
    title: 'Their name, not ours',
    body:
      'Every touchpoint reinforces the partner\u2019s brand. Our ego stays out of the room. The best compliment: a client\u2019s customer doesn\u2019t know we exist.',
  },
  {
    icon: Brain,
    title: 'Intelligence, not data',
    body:
      'Anyone can sell a contact list. We deliver researched dossiers with dual scores, buying committee maps and engagement playbooks. The gap between data and intelligence is the entire value of this company.',
  },
  {
    icon: Rocket,
    title: 'Expand from strength',
    body:
      'We own publisher revenue intelligence first. Then we earn the right to expand. Every new product must be as specific and defensible as the first.',
  },
];

const About = () => {
  useSeo({
    title: 'About 5DBS — Who we are & who we help',
    description: 'Meet the team behind 5DBS. We help B2B publishers and media brands turn their audience into a profitable, direct-sold lead-generation business — with every lead verified before delivery.',
    path: '/about',
  });
  return (
    <div className="min-h-screen bg-white font-sans text-navy">
      <Navbar />
      <main>
        {/* Hero */}
        <section
          className="relative min-h-[70vh] flex items-center overflow-hidden pt-28"
          style={{
            backgroundImage:
              "linear-gradient(180deg, rgba(20,30,52,0.86) 0%, rgba(20,30,52,0.94) 100%), url('https://images.unsplash.com/photo-1522204523234-8729aa6e3d5f?w=1920&q=80')",
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="relative max-w-6xl mx-auto w-full px-6 lg:px-8 py-24">
            <p className="text-brand-blue-light text-[12px] font-bold tracking-[0.16em] uppercase mb-5">
              The story
            </p>
            <h1 className="text-white font-black tracking-[-0.01em] leading-[1.05] text-[44px] sm:text-[58px] md:text-[68px] max-w-[900px]">
              We noticed something that didn't make sense.
            </h1>
            <p className="mt-7 text-slate-200/90 text-[17px] leading-[1.7] max-w-[720px]">
              B2B publishers sit on the most valuable audiences in the world. Millions of professionals who trust them — who open their emails, attend their events, read their research. That trust took decades to build.
            </p>
            <p className="mt-4 text-slate-200/90 text-[17px] leading-[1.7] max-w-[720px]">
              And yet, when those same publishers' advertisers need leads, they take their budget somewhere else — to vendors with no relationship, no editorial trust, no brand equity.
            </p>
            <p className="mt-6 text-white text-[19px] font-bold leading-[1.5] max-w-[720px]">
              The money exists. It's being spent. It's just going to someone who doesn't deserve the relationship.
            </p>
          </div>
        </section>

        {/* Origin */}
        <section className="bg-white py-20 md:py-24">
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <p className="text-brand-blue text-[12px] font-bold tracking-[0.16em] uppercase mb-4">
              How we started
            </p>
            <h2 className="text-navy font-black tracking-[-0.01em] leading-[1.15] text-[30px] sm:text-[36px] md:text-[42px]">
              We didn't just build a service. We built the intelligence layer that makes it possible.
            </h2>
            <div className="mt-8 space-y-5 text-body-muted text-[16.5px] leading-[1.75]">
              <p>
                Most companies who tried to solve this problem hired people and threw them at spreadsheets. We built AI that researches every company, scores both opportunity and personalised fit, verifies every contact through multiple layers, and assembles a complete intelligence dossier — in seconds, not days.
              </p>
              <p>
                Then we wrapped that technology in a white-label model so publishers can sell it under their own brand, to their own advertisers, at their own margin. <span className="text-navy font-bold">Their name goes on everything. Ours goes on nothing.</span>
              </p>
              <p>
                That's how we started. But the intelligence engine we built isn't limited to publishing. It's a chassis for turning any trusted audience into a revenue machine. And we're just getting started.
              </p>
            </div>
          </div>
        </section>

        {/* Positioning — Material Kit FeaturesTwo pattern (2x2 grid + featured card) */}
        <section className="bg-brand-soft py-16 md:py-24">
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-14">
              <p className="text-brand-blue text-[12px] font-bold tracking-[0.16em] uppercase mb-4">
                Positioning
              </p>
              <h2 className="text-navy font-black tracking-[-0.01em] leading-[1.08] text-[36px] sm:text-[44px] md:text-[52px]">
                What we do. What we don't.<br />
                <span className="text-brand-blue">Why it matters now.</span>
              </h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
              {/* Left: 2x2 info-card grid */}
              <div className="lg:col-span-7">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-10">
                  {positioning.slice(1).map((p, i) => {
                    const Icon = p.icon;
                    const a = posAccent[p.accent];
                    return (
                      <Reveal key={p.tag} delay={i * 100}>
                        <div
                          className={`inline-flex items-center justify-center w-11 h-11 rounded-xl text-white bg-gradient-to-br ${a.icon} ${a.shadow}`}
                        >
                          <Icon size={20} strokeWidth={2.2} />
                        </div>
                        <p className={`mt-4 ${a.text} text-[11px] font-bold uppercase tracking-[0.16em]`}>
                          {p.tag}
                        </p>
                        <h3 className="mt-1.5 text-navy text-[18px] font-bold leading-snug">
                          {p.title}
                        </h3>
                        <p className="mt-2 text-body-muted text-[14px] leading-[1.7]">
                          {p.body}
                        </p>
                      </Reveal>
                    );
                  })}
                </div>
              </div>

              {/* Right: featured centered-blog card (What we do) */}
              <div className="lg:col-span-5 lg:ml-auto w-full">
                <Reveal
                  delay={200}
                  className="bg-white rounded-2xl shadow-mk-lg overflow-hidden hover:shadow-mk-lg hover:-translate-y-1 transition-all duration-300"
                >
                  <div
                    className="h-64 w-full bg-cover bg-center"
                    style={{
                      backgroundImage:
                        "url('https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1200&q=80')",
                    }}
                  />
                  <div className="p-7">
                    <p className="text-brand-blue text-[11px] font-bold uppercase tracking-[0.16em]">
                      What we do
                    </p>
                    <h3 className="mt-2 text-navy text-[22px] font-black leading-tight tracking-tight">
                      Publishers become demand-gen businesses overnight.
                    </h3>
                    <p className="mt-3 text-body-muted text-[14.5px] leading-[1.75]">
                      We turn B2B publishers into demand generation businesses overnight. White-label, fully operational, running under your brand. Your advertisers get qualified leads. You keep the margin. We handle everything else.
                    </p>
                    <a
                      href="/#call"
                      className="mt-5 inline-flex items-center h-10 px-5 rounded-md bg-brand-blue hover:bg-brand-blue-dark text-white text-[12px] font-bold uppercase tracking-[0.08em] shadow-mk-blue hover:shadow-mk-blue-hover hover:-translate-y-0.5 transition-all duration-300"
                    >
                      Find out more
                      <ArrowRight size={14} strokeWidth={2.5} className="ml-1.5" />
                    </a>
                  </div>
                </Reveal>
              </div>
            </div>
          </div>
        </section>

        {/* Stats band */}
        <section
          className="relative py-24"
          style={{
            backgroundImage:
              "linear-gradient(135deg, rgba(26,32,53,0.82) 0%, rgba(52,71,103,0.78) 100%), url('https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1920&q=80')",
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <p className="text-brand-blue-light text-[12px] font-bold tracking-[0.16em] uppercase mb-3">
                Publisher outcomes
              </p>
              <h2 className="text-white font-black tracking-[-0.01em] leading-[1.15] text-[30px] sm:text-[38px]">
                What that looks like in practice
              </h2>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {aboutStats.map((s, i) => (
                <Reveal
                  key={s.label}
                  delay={i * 110}
                  className="rounded-2xl bg-white/[0.06] backdrop-blur border border-white/10 p-6 sm:p-7"
                >
                  <div className="text-brand-blue-light text-[36px] sm:text-[40px] font-black leading-none tracking-tight">
                    <CountUp value={s.value} />
                  </div>
                  <p className="mt-4 text-white text-[14px] font-bold leading-[1.4]">{s.label}</p>
                  {s.sub && (
                    <p className="mt-1 text-slate-400 text-[12.5px] leading-[1.4]">{s.sub}</p>
                  )}
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* Manifesto */}
        <section
          className="relative py-28 md:py-32 overflow-hidden"
          style={{
            background:
              'linear-gradient(135deg, #0C0E12 0%, #14161B 60%, #1A2035 100%)',
          }}
        >
          <div
            className="absolute inset-0 opacity-[0.06] pointer-events-none"
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><circle cx='60' cy='60' r='1' fill='%23ffffff'/></svg>\")",
            }}
          />
          <div className="relative max-w-3xl mx-auto px-6 lg:px-8 text-center">
            <p className="text-brand-blue-light text-[12px] font-bold tracking-[0.16em] uppercase mb-6">
              Manifesto
            </p>
            <p className="text-white font-black text-[30px] sm:text-[38px] md:text-[44px] leading-[1.2]">
              Your advertisers are already buying leads.
            </p>
            <p className="mt-3 text-brand-blue-light font-black text-[30px] sm:text-[38px] md:text-[44px] leading-[1.2]">
              Just not from you.
            </p>
            <div className="mt-10 space-y-5 text-slate-300 text-[16px] leading-[1.85] max-w-2xl mx-auto">
              <p>
                They're buying from vendors who've never met their audience. Who don't know the market. Who couldn't name a single subscriber.
              </p>
              <p>
                Meanwhile, you have something no vendor can build: <em className="text-white">trust that took decades to earn.</em>
              </p>
              <p>
                We built an intelligence engine that turns that trust into revenue. AI that researches every company, scores every opportunity, verifies every contact, and delivers a complete dossier — all under your brand.
              </p>
              <p className="text-white font-bold text-[18px]">
                Your name on every deliverable. Your margin on every deal. Our engine running quietly underneath.
              </p>
              <p>
                We don't need our name in the room. We need yours to <em className="text-white">mean more</em> when we leave it.
              </p>
              <p className="text-brand-blue-light font-bold italic pt-4">That's 5DBS.</p>
            </div>
          </div>
        </section>

        {/* Principles — Material Kit FeaturesOne pattern (2-col: heading + stacked options) */}
        <section className="bg-white py-16 md:py-24">
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
              {/* Left: heading, body, link */}
              <div className="lg:col-span-5">
                <p className="text-brand-blue text-[12px] font-bold tracking-[0.16em] uppercase mb-4">
                  Principles
                </p>
                <h2 className="text-navy font-black tracking-[-0.01em] leading-[1.12] text-[32px] sm:text-[38px] md:text-[42px]">
                  How we build.<br />How we sound.
                </h2>
                <p className="mt-5 text-body-muted text-[15.5px] leading-[1.75]">
                  Five ideas that decide every line of code, every line of copy, and every promise on every deliverable. If a decision doesn't sit on one of these, we don't make it.
                </p>
                <a
                  href="/#call"
                  className="mt-6 inline-flex items-center text-brand-blue text-[14px] font-semibold group"
                >
                  See how it lands in practice
                  <ArrowRight
                    size={16}
                    strokeWidth={2.4}
                    className="ml-1.5 transition-transform duration-300 ease-out group-hover:translate-x-1"
                  />
                </a>
              </div>

              {/* Right: stacked options */}
              <div className="lg:col-span-7">
                <ul className="divide-y divide-slate-100">
                  {principles.map((p, i) => {
                    const Icon = p.icon;
                    return (
                      <Reveal
                        key={p.title}
                        as="li"
                        delay={i * 90}
                        className="flex gap-5 py-5 first:pt-0 last:pb-0"
                      >
                        <div className="shrink-0 mt-1">
                          <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-brand-blue/10 text-brand-blue">
                            <Icon size={20} strokeWidth={2.2} />
                          </div>
                        </div>
                        <div>
                          <h3 className="text-navy text-[17px] font-bold leading-snug">
                            {p.title}
                          </h3>
                          <p className="mt-1.5 text-body-muted text-[14.5px] leading-[1.7]">
                            {p.body}
                          </p>
                        </div>
                      </Reveal>
                    );
                  })}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section
          className="relative py-24"
          style={{ background: 'linear-gradient(135deg, #1A2035 0%, #344767 100%)' }}
        >
          <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="text-white font-black tracking-[-0.01em] leading-[1.15] text-[30px] sm:text-[38px]">
              How many deals do you want in your pipeline before most vendors finish onboarding?
            </h2>
            <div className="mt-9">
              <Link
                to="/contact"
                data-testid="about-grab-15-btn"
                onClick={() => track(events.GRAB_15_CALL_CLICK, { location: 'about' })}
                className="inline-flex items-center justify-center h-12 px-8 rounded-md bg-brand-pink hover:bg-brand-pink/90 text-white text-[13px] font-bold uppercase tracking-[0.08em] shadow-mk hover:shadow-mk-lg hover:-translate-y-0.5 transition-all duration-300"
              >
                Grab a 15-minute call
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default About;
